import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type AddProposal,
  type InvoiceImportResult,
  type RegistryProposalsResponse,
} from '@k-management/shared';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { apiRequest } from '../../lib/api.js';
import {
  CreateDialog,
  documentsOf,
  LinkDialog,
  proposalKeys,
  ROLE_NAMES,
  RolePills,
  SubjectName,
} from './RegistryProposalsPage.js';
import { treasuryKeys } from './treasury-ui.js';

interface Handled {
  key: string;
  name: string;
  kind: 'link' | 'create';
  text: string;
}

/** The documents still to link or to enter in the Anagrafica: what the window asks about. */
export function pendingSubjects(found: RegistryProposalsResponse): number {
  return found.link.length + found.add.length + found.manual.length;
}

export const fetchProposals = () =>
  apiRequest<RegistryProposalsResponse>('/treasury/registry-proposals');

/**
 * At the end of a reading of Gestify (1.78): the counterparties with a VAT
 * number or tax code already in the Anagrafica under another name, whose
 * invoices the reading did not link, and those the Anagrafica does not have.
 * Each is linked, entered or left for later («Non ora»: it stays in
 * Tesoreria › Anagrafica dalle fatture).
 */
export function RegistryCheckDialog({
  result,
  onClose,
}: {
  result: InvoiceImportResult;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [later, setLater] = useState<string[]>([]);
  const [handled, setHandled] = useState<Handled[]>([]);
  const [creating, setCreating] = useState<AddProposal | null>(null);
  const [linking, setLinking] = useState<AddProposal | null>(null);
  const data = useQuery({ queryKey: proposalKeys, queryFn: fetchProposals });

  useEffect(() => {
    if (!dialogRef.current?.open) dialogRef.current?.showModal();
  }, []);

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: proposalKeys });
    await queryClient.invalidateQueries({ queryKey: treasuryKeys.all });
  };
  const found = data.data;
  const shown = (items: AddProposal[]) => items.filter((item) => !later.includes(item.key));
  const done = (item: AddProposal, kind: Handled['kind'], text: string) =>
    setHandled((current) => [...current, { key: item.key, name: item.name, kind, text }]);
  const linked = handled.filter((item) => item.kind === 'link');
  const created = handled.filter((item) => item.kind === 'create');
  const notNow = (item: AddProposal) => (
    <button type="button" className="small" onClick={() => setLater([...later, item.key])}>
      Non ora
    </button>
  );
  return (
    <dialog ref={dialogRef} className="dialog wide-dialog" onClose={onClose}>
      <div className="stack">
        <h2>Fatture lette da Gestify: controllo Anagrafica</h2>
        <p>
          Importate: {result.created} nuove, {result.changed} aggiornate, {result.removed} tolte.
          {found &&
            ` ${found.link.length} controparti hanno una partita IVA o un codice fiscale già in Anagrafica con un altro nome e ${found.add.length + found.manual.length} non sono in Anagrafica: le loro fatture restano senza soggetto finché non decidi.`}
        </p>
        {data.isPending && <p className="muted">Caricamento…</p>}
        {data.isError && <p className="error">{data.error.message}</p>}
        {found && (
          <>
            <h3>Da collegare</h3>
            {shown(found.link).length === 0 && linked.length === 0 ? (
              <p className="muted small">Niente da collegare.</p>
            ) : (
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Nome su Gestify</th>
                      <th>Partita IVA / CF</th>
                      <th>Documenti</th>
                      <th>In Anagrafica</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {shown(found.link).map((item) => {
                      const newRoles = item.newRoles.map((role) => ROLE_NAMES[role]).join(' e ');
                      return (
                        <tr key={item.key}>
                          <td>
                            <strong>{item.name}</strong>
                          </td>
                          <td>{[item.vatNumber, item.taxCode].filter(Boolean).join(' · ')}</td>
                          <td>{documentsOf(item)}</td>
                          <td>
                            <SubjectName id={item.subject!.id} name={item.subject!.name} />
                            {' · '}
                            {item
                              .subject!.roles.map((role) => ROLE_NAMES[role as 'C' | 'F'] ?? role)
                              .join(', ') || 'senza ruoli'}
                            <span className="block">
                              {newRoles ? (
                                <span className="pill warn">diventa anche {newRoles}</span>
                              ) : (
                                <span className="pill ok">ruolo già presente</span>
                              )}
                            </span>
                          </td>
                          <td>
                            <div className="row">
                              <button
                                type="button"
                                className="primary small"
                                onClick={() => setLinking(item)}
                              >
                                {newRoles ? `Collega e aggiungi ${newRoles}` : 'Collega'}
                              </button>
                              {notNow(item)}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {linked.map((item) => (
                      <tr key={item.key}>
                        <td>
                          <strong>{item.name}</strong>
                        </td>
                        <td colSpan={4}>
                          <span className="pill ok">✓ {item.text}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <h3>Nuovi soggetti</h3>
            {shown([...found.add, ...found.manual]).length === 0 ? (
              <p className="muted small">Nessun soggetto da creare.</p>
            ) : (
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Nome su Gestify</th>
                      <th>Partita IVA / CF</th>
                      <th>Documenti</th>
                      <th>Diventa</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {shown([...found.add, ...found.manual]).map((item) => {
                      const withId = Boolean(item.vatNumber || item.taxCode);
                      return (
                        <tr key={item.key}>
                          <td>
                            <strong>{item.name}</strong>
                            {item.sameName && (
                              <small className="muted block">
                                stesso nome di «{item.sameName.name}»
                                {item.sameName.vatNumber
                                  ? ` (P. IVA ${item.sameName.vatNumber})`
                                  : ''}
                                {withId ? ': soggetti diversi' : ''}
                              </small>
                            )}
                          </td>
                          <td>
                            {[item.vatNumber, item.taxCode].filter(Boolean).join(' · ') || '—'}
                          </td>
                          <td>{documentsOf(item)}</td>
                          <td>
                            <RolePills roles={item.roles} />
                          </td>
                          <td>
                            <div className="row">
                              {!withId && (
                                <button
                                  type="button"
                                  className="small"
                                  onClick={() => setLinking(item)}
                                >
                                  Collega a…
                                </button>
                              )}
                              <button
                                type="button"
                                className={withId ? 'primary small' : 'small'}
                                onClick={() => setCreating(item)}
                              >
                                Crea soggetto
                              </button>
                              {notNow(item)}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {created.length > 0 && (
              <ul className="small">
                {created.map((item) => (
                  <li key={item.key}>
                    <span className="pill ok">✓</span> {item.name}: {item.text}
                  </li>
                ))}
              </ul>
            )}
            <p className="muted small">
              «Collega» porta le fatture al soggetto, il nome di Gestify diventa un suo alias (le
              prossime letture lo collegano da sole) e, se manca, aggiunge il ruolo dei documenti.
              «Crea soggetto» apre la scheda già compilata. «Non ora» le lascia senza soggetto: le
              ritrovi in Tesoreria › Anagrafica dalle fatture.
            </p>
            {found.roles.length + found.data.length + found.aliases.length > 0 && (
              <p className="notice small">
                Altro da vedere in{' '}
                <Link to="/tesoreria/anagrafica-dalle-fatture" onClick={onClose}>
                  Anagrafica dalle fatture
                </Link>
                : {found.roles.length} ruoli dai documenti · {found.data.length} dati ·{' '}
                {found.aliases.length} alias.
              </p>
            )}
          </>
        )}
        <div className="actions">
          <button type="button" onClick={onClose}>
            Chiudi
          </button>
        </div>
      </div>

      {creating && found && (
        <CreateDialog
          proposal={creating}
          types={found.types}
          onClose={() => setCreating(null)}
          onSaved={async () => {
            done(creating, 'create', 'creato in Anagrafica, fatture collegate');
            setCreating(null);
            await refresh();
          }}
        />
      )}
      {linking && found && (
        <LinkDialog
          proposal={linking}
          subjects={found.subjects}
          onClose={() => setLinking(null)}
          onSaved={async (subjectId) => {
            const name =
              found.subjects.find((item) => item.id === subjectId)?.name ?? 'il soggetto';
            done(linking, 'link', `collegata a ${name} · alias «${linking.name}»`);
            setLinking(null);
            await refresh();
          }}
        />
      )}
    </dialog>
  );
}
